<template>
  <v-menu
    v-model="isMenuOpen"
    offset-y
    nudge-bottom="5"
    class="pt-0"
    max-width="350"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-on="on"
        outlined
        color="blue-grey lighten-2"
        class="mx-0 px-2 py-2 mt-2 mr-3"
        data-tourid="explore-userFilter-button"
        @click="
          trackFilterActions('my_filters.open_menu', { section: $route.name })
        "
      >
        <v-icon color="blue-grey darken-1" style="margin-left: 1px;" left
          >mdi-star-outline</v-icon
        >
        <span class="blue-grey--text text--darken-3 caption">
          {{ $t('filters.my_filter_other') }}
        </span>
        <v-icon color="blue-grey darken-1" right>mdi-menu-down</v-icon>
      </v-btn>
    </template>
    <v-card class="px-1 pt-0" :loading="isLoading">
      <v-text-field
        dense
        flat
        solo
        hide-details
        class="search-input-filters pt-1 body2 mb-n1"
        prepend-icon="mdi-magnify"
        v-model="terms"
        :placeholder="$t('common.search')"
      ></v-text-field>
      <v-list v-if="hasUserFilters">
        <v-list-item
          v-for="filter in userFilters"
          dense
          class="rounded"
          :key="filter._id"
          :class="{ 'selected-item': activeUserFilter === filter._id }"
          @click="onSelectFilter(filter)"
        >
          <div v-if="isEditable" class="pinned-icon">
            <v-icon
              v-if="filter.isPinned"
              :color="activeUserFilter === filter._id ? 'blue' : 'blue-grey'"
              class="mr-2"
              size="14"
              >mdi-pin</v-icon
            >
          </div>
          <text-highlight
            highlightClass="yellow lighten-1"
            class="body2 pr-3 mx-0 blue-grey--text text--darken-3 text-truncate"
            highlightComponent="span"
            :queries="highlightQueries"
            :title="filter.name"
            >{{ filter.name }}</text-highlight
          >
        </v-list-item>
        <v-list-item
          v-if="isEditable"
          dense
          inactive
          class="rounded"
          :ripple="false"
          @click="onEditUserFilters"
        >
          <v-list-item-title class="pr-3 blue--text cursor-pointer ml-n1">
            <span>{{ $t('explore.manage_my_filters') }}</span>
          </v-list-item-title>
        </v-list-item>
      </v-list>
      <EmptyState
        v-else
        class="px-6 py-6"
        icon="mdi-ghost-outline"
        :iconSize="24"
        :title="
          terms === null
            ? $t('explore.no_filters_to_show')
            : $t('common.no_results_found')
        "
        :message="
          terms === null
            ? $t('explore.no_filters_to_show_text')
            : $t('common.no_matches_text')
        "
      />
    </v-card>
  </v-menu>
</template>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import EmptyState from '@/components/EmptyState.vue'

export default {
  components: {
    EmptyState,
  },

  props: {
    isEditable: {
      type: Boolean,
      default: true,
    },
  },

  mixins: [mixin, trackingMixin],

  data() {
    return {
      userFilters: [],
      terms: null,
      isLoading: false,
      isMenuOpen: false,
    }
  },

  created() {
    this.fetchUserFilters()
  },

  computed: {
    ...mapGetters(['organizationId']),

    activeUserFilter() {
      return this.$route.query.userFilter || ''
    },

    hasUserFilters() {
      return this.userFilters?.length
    },

    highlightQueries() {
      return [this.terms ?? '', ...(this.terms?.split(' ') || [])]
    },
  },

  methods: {
    async fetchUserFilters() {
      try {
        this.isLoading = true
        const response = await api.userFilter.getAll(this.organizationId, {
          limit: 7,
          sort: { by: 'name', order: 'asc' },
          terms: this.terms,
        })
        this.userFilters = response?.data?.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    onSelectFilter(filter) {
      this.isMenuOpen = false
      this.$emit('onSelectFilter', {
        userFilter: filter._id,
        userFilterName: filter.name,
        ..._.omit(filter.filters, '_id'),
      })
    },

    onEditUserFilters() {
      this.isMenuOpen = false
      this.$emit('openDrawer')
      this.trackFilterActions('my_filters.manage_filters')
    },
  },

  watch: {
    terms: _.debounce(function() {
      this.fetchUserFilters()
      this.trackFilterActions('my_filters.search_menu', this.terms)
    }, 1000),
  },
}
</script>

<style lang="scss">
.selected-item {
  background: $color-blue-lighten2;
  .v-list-item__title {
    color: $color-blue;
  }
}
.search-input-filters .v-input__prepend-outer {
  margin-right: 0px !important;
  margin-left: 5px !important;
}
.pinned-icon {
  position: absolute;
  right: 0px;
  top: 1px;
}
</style>
